import './PlaceList.scss'

import List, { DividerProps } from 'components/App/components/List'
import ListItem from 'components/App/components/ListItem'
import { ScrollableContainer } from 'components/App/components/ScrollableContainer'
import { SortOrder } from 'components/Collections/data/model'
import {
  CollectionCategory,
  getCollectionCategoryUrlPath,
} from 'components/Collections/util/collection-category'
import { useAppSelector } from 'data/store/hooks'
import { i18n } from 'lib/i18n'
import { Position } from 'lib/place'
import React, { useCallback, useEffect, useState } from 'react'
import { calculateDistance } from 'utils/formatters'

import PlaceHeader from './PlaceHeader'
import PlaceItem from './PlaceItem'

export interface PlaceListProps {
  collection: Collection
  places: Place[]
  onClose: () => void
  onHover?: (placeId?: string) => void
}

export interface Collection {
  name: string
  description?: string
  id: string
  category?: CollectionCategory
  placesCount: number
}

export interface Place {
  id: string
  pdsUrl: string
  distanceFromPoint?: string
  position: Position
  name: string
  imageUrl?: string
  address: string
  reviewCount?: number
  rating?: number
  ratingLink?: string
  categoryId?: string
  createdTime: number
  updatedTime: number
}

const PlaceList = ({ collection, places, onClose, onHover }: PlaceListProps): JSX.Element => {
  const [sortedPlaces, setSortedPlaces] = useState(places)
  const sortOrder = useAppSelector((state) => state.collections.order)
  const imageCategoryUrl =
    collection.category !== undefined
      ? getCollectionCategoryUrlPath(collection.category)
      : getCollectionCategoryUrlPath(CollectionCategory.category1)
  const dividerOptions: DividerProps = { position: { left: '1rem', right: '1rem' } }
  const { coordinates: userCurrentPosition } = useAppSelector((state) => state.user.currentPosition)

  const handleSort = useCallback(() => {
    setSortedPlaces((prevState) => {
      const prevPlaces = [...prevState]

      prevPlaces.sort((a, b) => {
        switch (sortOrder) {
          case SortOrder.dateAdded:
            if (a.createdTime < b.createdTime) return 1
            if (a.createdTime === b.createdTime) return 0
            return -1
          case SortOrder.distance:
            if (userCurrentPosition !== null) {
              const distanceFromA = calculateDistance(userCurrentPosition, a.position)
              const distanceFromB = calculateDistance(userCurrentPosition, b.position)
              return distanceFromA > distanceFromB ? 1 : -1
            }
            return 0
          default:
            return a.name.localeCompare(b.name)
        }
      })

      return prevPlaces
    })
  }, [sortOrder, userCurrentPosition])

  useEffect(() => {
    handleSort()
  }, [sortOrder, handleSort])

  return (
    <div className="PlaceList">
      <PlaceHeader
        title={collection.name}
        count={collection.placesCount}
        subtitle={collection.description}
        backgroundImageUrl={imageCategoryUrl}
        onClose={onClose}
      />
      <div className="PlaceList__items">
        {sortedPlaces.length > 0 ? (
          <ScrollableContainer className="PlaceList__scroll-container">
            <List divider={dividerOptions}>
              {sortedPlaces.map((place) => (
                <ListItem
                  key={place.id}
                  onMouseEnter={() => onHover && onHover(place.id)}
                  onMouseLeave={() => onHover && onHover()}
                >
                  <PlaceItem {...place} />
                </ListItem>
              ))}
            </List>
          </ScrollableContainer>
        ) : (
          <div className="PlaceList__empty"> {i18n('collections.place-list.empty')} </div>
        )}
      </div>
    </div>
  )
}

export default PlaceList
